import { Input, ModalDialog, SelectPropsValue } from 'commons/components';
import { PocButton } from 'commons/components/components-wrapper/poc-button.component';
import { GraphqlError, executeRequest } from '@root/src/core/utils/backend-service.ts';
import { DeviceConfigurationForm } from '@root/src/modules/devices/domain/models/device-configuration-form.ts';
import { usePocMutation } from 'core/custom-hooks/use-poc-mutation.hook';
import { useTranslation } from 'core/i18n/i18n';
import { settingsKeys } from 'core/query-client/keys/settings.keys';
import { updateQuery } from 'core/query-client/query-client';
import { DeviceFormValidation } from 'modules/devices/pods/private-pods/devices-add-edit/types/device-form';
import { handleErrors } from 'modules/devices/utils/utils';
import { copyConfigurationInstance } from 'modules/settings/pods/settings-devices/settings-devices-configuration/service/settings-devices-configuration.service';
import { DeviceConfigurationInfo } from 'modules/settings/pods/settings-devices/settings-devices-configuration/settings-devices-configuration-list/settings-devices-configuration-list.model';
import { useState } from 'react';
import { CONFIGURATION_NAME_MAX_LENGTH } from '../settings-devices-configuration-layout/constants';
import {
    COPY_FORM_ERRORS_MAPPINGS,
    customErrorMessages,
    getCustomFormError,
    newDeviceCopyFormValidation,
} from './settings-devices-configuration-create-from-modal.validation';

export interface SettingsDevicesConfigurationCreateFromModalProps {
    baseConfiguration: DeviceConfigurationInfo;
    onCopyNewConfiguration: (newConfigurationId: string) => void;
}

export const SettingsDevicesConfigurationCreateFromModal: React.FC<SettingsDevicesConfigurationCreateFromModalProps> = (
    props
) => {
    const { baseConfiguration, onCopyNewConfiguration } = props;
    const [form, setForm] = useState<DeviceConfigurationForm>({
        existingInstanceId: baseConfiguration.id,
        description: baseConfiguration.description,
        name: `${baseConfiguration.name} - Copy `.slice(0, CONFIGURATION_NAME_MAX_LENGTH),
    });
    const [deviceCopyFormError, setDeviceCopyFormError] = useState<GraphqlError[]>([]);
    const [deviceCopyFormValidatorError, setDeviceCopyFormValidatorError] = useState<Partial<DeviceFormValidation>>(
        structuredClone(newDeviceCopyFormValidation)
    );

    const [isOpen, setIsOpen] = useState<boolean>(true);
    const { t } = useTranslation();

    const { mutate: copyConfiguration, isPending: loading } = usePocMutation({
        mutationFn: () => executeRequest(() => copyConfigurationInstance(form)),
        onSuccess: (result: string) => {
            updateQuery(settingsKeys.configurations());
            onCopyNewConfiguration(result);
            setIsOpen(false);
        },
        onError: (error) => {
            handleErrors(error, COPY_FORM_ERRORS_MAPPINGS, setDeviceCopyFormError, setDeviceCopyFormValidatorError);
        },
    });

    const onSave = () => copyConfiguration();

    const onChange = (field: string, value: SelectPropsValue) => setForm({ ...form, [field]: value });

    return (
        <ModalDialog
            title={t('modules.devices.pods.devices-list.copy-from-modal.title', {
                configuration: baseConfiguration.name,
            })}
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            loading={loading}
            footerOptions={
                <PocButton
                    variant="primary"
                    size="medium"
                    onClick={onSave}
                    disabled={form.name === '' || form.name === undefined}
                >
                    {t('modules.devices.pods.devices-list.copy-from-modal.save')}
                </PocButton>
            }
        >
            <Input
                label={t('modules.devices.pods.devices-list.copy-from-modal.name')}
                name={'name'}
                placeholder={t('modules.devices.pods.devices-list.copy-from-modal.name')}
                required
                spaceForError
                maxlength={16}
                value={form?.name}
                noClearIcon={false}
                validationMessages={customErrorMessages(t).name}
                customValidationCode={getCustomFormError(deviceCopyFormError, 'name')[0]}
                validity={deviceCopyFormValidatorError.name}
                onChange={(field: string, value: SelectPropsValue) => onChange(field, value)}
            />

            <Input
                label={t('modules.devices.pods.devices-list.copy-from-modal.description')}
                name={'description'}
                placeholder={t('modules.devices.pods.devices-list.copy-from-modal.description')}
                spaceForError
                maxlength={30}
                value={form?.description}
                noClearIcon={false}
                onChange={(field: string, value: SelectPropsValue) => onChange(field, value)}
            />
        </ModalDialog>
    );
};
